import { IUSaveSpecOption } from './specs';

export type BuildStatusValue =
  | 'New'
  | 'BuildApproved'
  | 'ContractAccepted'
  | 'TenDayComplete'
  | 'Closing'
  | 'Complete'
  | 'Deleted';

export interface IBuild {
  agents: {
    agentId: number;
    firstName: string;
    lastName: string;
  }[];
  buildId: number;
  neighborhoodId: number;
  neighborhood: string;
  neighborhoodName: string;
  lotNumber: number;
  alert: string;
  pendingDeletionAfter: string;
  status: BuildStatusValue;
  buyer: string;
  isSpec: boolean;
  specId: number;
  contractAcceptedOn?: string;
  optionLockoutDate?: string;
  phase?: string;
  fieldWalkthroughId: string;
  planId: number;
  plan: string;
  isTenDayComplete: boolean;
  planDescription?: string;
  personId?: number;
  estimatedCompletionDate: string;
  territory: string;
  jobType?: string;
  hasSitePlanFile: boolean;
  hasContractFile: boolean;
  isHoaal: boolean;
  address?: string;
  isInWarranty?: boolean;
}

export interface IBuildRes {
  records: IBuild[];
  totalRecordCount: number;
}

export interface IBuildStatus {
  value: string;
  description: string;
  shorthand: string;
}

export interface IBuildPost {
  neighborhoodId: number;
  lotNumber: number;
  planId: number;
  planElevationId: number;
  personId?: number;
  price: number;
  contractName?: string;
}

export interface IBuildTenDayDetails {
  buildId: number;
  neighborhoodDescription: string;
  lotNumber: number;
  planDescription: string;
  clientName: string;
  selectionsComplete: boolean;
  optionLockoutDate: string;
  basePrice: IPriceDetails;
  lotPremium: IPriceDetails;
  agentReduction: number;
  contractAcceptedAmount: number;
  appraisalAlarmValue: number;
  contractUpgradeAmount: number;
  tenDaySelectedAtContractUpgradeAmount: number;
  additionalClosingPrice: number;
  contractPrice: number;
  contractPriceAdjusted: number;
  tenDayUpgradeAmount: number;
  totalUpgradeAmount: IPriceDetails;
  optionCapOverage: IPriceDetails;
  upgradesSelectedAtContract: IPriceDetails;
  totalPrice: number;
  totalPriceAdjusted: number;
  upgradeCapOverage: IPriceDetails;
  buildStatus: string;
  contractAdjustmentReason: string;
  contractOverridesLocked: boolean;
  optionsAtContract: number;
  incentive: number;
  salesIncentives: ISalesIncentive[];
  salesContractingType: 'Scan' | 'Digital';
}

export interface ISalesIncentive {
  recordId: number;
  incentiveId: number;
  name: string;
  incentiveAmount: number;
}

export interface IContractAdjustment {
  basePrice: IPriceDetails;
  lotPremium: IPriceDetails;
  optionCapOverage: IPriceDetails;
  incentive: number;
  contractAdjustmentReason: string;
  incentiveTypes: ISalesIncentive[];
}

export interface IPriceDetails {
  defaultValue: number | string;
  adjusted: number | string;
  adjustment: number | string;
  adjustedAnchor?: boolean;
  lastSavedDefaultValue?: number | string;
  needsUpdate?: boolean;
}

export interface IBuildTenDayLockoutDatePut {
  optionLockoutDate: string;
}

export interface IBuildContingencyPost {
  hasHouseSaleContingency: boolean;
  houseSaleContingencyLocation: string;
}

export interface IUpdateBuild {
  selectedOptions: IUSaveSpecOption[];
}

export interface IBuildCeremonies {
  buildId: number;
  orientationDate: Date;
  celebrationDate: Date;
  closingDate: Date;
  lotNumber: number;
  neighborhood: string;
}

export interface ILenderPayload {
  financingType: string;
  lenderId: number | string;
}

export const buildDocuments = {
  FinalContractPacket: 'FinalContractPacket',
  FinalPriceContract: 'FinalPriceContract',
  Contract: 'Contract',
  AddendumA: 'AddendumA',
  AddendumB: 'AddendumB',
  AddendumC: 'AddendumC',
  AddendumD: 'AddendumD',
  AddendumE: 'AddendumE',
  PlanSignOff: 'PlanSignOff',
  AddendumF: 'AddendumF',
  ContractOffer: 'ContractOffer',
  AcceptedContractOffer: 'AcceptedContractOffer',
  FinalContract: 'FinalContract',
  ClosingDocuments: 'ClosingDocuments',
  ElevationImage: 'ElevationImage',
  InteriorImage: 'InteriorImage',
  InsulationPhoto: 'InsulationPhoto',
  AddendumG: 'AddendumG',
  AddendumH: 'AddendumH',
  AddendumI: 'AddendumI',
  TabletReleaseForm: 'TabletReleaseForm',
  BuyerAcknowledgementStatement: 'BuyerAcknowledgementStatement',
  AddendumJ: 'AddendumJ',
  AddendumK: 'AddendumK',
  ContractPacket: 'ContractPacket',
  LenderLetter: 'LenderLetter'
} as const;

export type buildDocumentsType = (typeof buildDocuments)[keyof typeof buildDocuments];

export interface ITestBuild {
  buildId: number;
  buildCreated: string;
  neighborhoodName: string;
  plan: string;
  lotNumber: number;
  contractName: string;
}

export interface IBuildNotes {
  buildId: number;
  notes: string;
}
